import type { ReactNode } from 'react';
import { Button } from '@newcore/ui/buttons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { DisplaySizes } from '@newcore/ui/enums/typography-display-sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Display, Text } from '@newcore/ui/typography';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { useMobileInstallApp } from '@/features/onboarding/hooks/use-mobile-install-app';
import { getNewcoreAppIconUrl } from '@/features/onboarding/utils/install-page-assets';

const APP_NOT_INSTALLED_NOTICE =
  'The NewCore app isn’t installed on this device. Install it, then tap Open NewCore again.';
const DEFAULT_CTA_LABEL = 'Open NewCore';
const ALREADY_INSTALLED_LABEL = 'Already installed? Open the app';

export interface StoreSlots {
  storeName: string;
  isIos: boolean;
}

interface MobileInstallAppProps {
  universalLinkUrl: string;
  deepLinkUrl: string;
  title: string;
  children: (slots: StoreSlots) => ReactNode;
  tenantDisplayName?: string;
  ctaLabel?: string;
  notice?: string;
}

function storeName(showAppStore: boolean, showPlayStore: boolean) {
  if (showAppStore && showPlayStore) {
    return 'the App Store or Google Play';
  }

  return showAppStore ? 'the App Store' : 'Google Play';
}

export function MobileInstallApp({
  universalLinkUrl,
  deepLinkUrl,
  title,
  tenantDisplayName,
  ctaLabel = DEFAULT_CTA_LABEL,
  notice,
  children,
}: MobileInstallAppProps) {
  const { showPlayStore, showAppStore, isAppMissing, handleOpenApp } =
    useMobileInstallApp({ deepLinkUrl });

  return (
    <div className='h-dvh overflow-y-auto bg-surface-100'>
      <div className='mx-auto flex min-h-full w-full max-w-md flex-col px-6 pb-6 pt-10 text-center'>
        <div className='flex flex-col items-center gap-5'>
          <img
            src={getNewcoreAppIconUrl()}
            alt='NewCore'
            className='size-20 rounded-[18px]'
          />
          <div className='flex flex-col gap-2.5'>
            <Display
              as='h1'
              size={DisplaySizes.SM}
              weight={FontWeights.REGULAR}
              className='font-manrope leading-[1.2] tracking-[-0.28px] text-ink-800'
            >
              {title}
            </Display>
            {tenantDisplayName && (
              <Text
                as='p'
                size={TextSizes.MD}
                className='font-inter leading-[1.4] tracking-[-0.16px] text-ink-500'
                data-testid='mobile-install-app-subtitle'
              >
                You were invited to {tenantDisplayName}.
              </Text>
            )}
          </div>
        </div>

        {notice && (
          <Text
            as='p'
            size={TextSizes.MD}
            className='mt-4 text-tertiary'
            data-testid='mobile-install-app-notice'
          >
            {notice}
          </Text>
        )}

        {isAppMissing && (
          <Onboarding.Error
            error={APP_NOT_INSTALLED_NOTICE}
            className='mt-6 text-left'
          />
        )}

        <ol
          className='mt-8 flex flex-col gap-[18px] rounded-[20px] bg-surface-50 p-5 text-left'
          data-testid='install-steps'
        >
          {children({
            storeName: storeName(showAppStore, showPlayStore),
            isIos: showAppStore && !showPlayStore,
          })}
        </ol>

        <div className='mt-auto flex flex-col gap-3 pt-8'>
          <Button
            asChild
            size={Sizes.LG}
            className='h-auto w-full rounded-[40px] bg-ink-800 px-5 py-3.5 text-white hover:not-disabled:bg-ink-800'
          >
            <a href={universalLinkUrl} data-testid='open-app-button'>
              <Text
                as='span'
                size={TextSizes.LG}
                weight={FontWeights.SEMIBOLD}
                className='font-manrope leading-[22px]'
              >
                {ctaLabel}
              </Text>
            </a>
          </Button>
          <Button
            variant={ButtonVariants.LINK_SECONDARY}
            onClick={handleOpenApp}
            data-testid='already-installed-button'
          >
            {ALREADY_INSTALLED_LABEL}
          </Button>
        </div>
      </div>
    </div>
  );
}
